<template>
	<header
		class="header"
		:class="{
			'header--sidenav-open': isSideNavOpen,
		}"
	>
		<div class="header__top-center" v-if="showIcon">
			<NuxtLinkLocale to="/home/" :aria-label="ariaHomeLink">
				<PictureImg
					class="header__logo"
					:class="{
						'header__logo--scroll': scrollLogo,
						'header__logo--scrolled': isScrolled,
					}"
					:media="showIcon"
				/>
			</NuxtLinkLocale>
		</div>
		<div class="header__top-left">
			<SideNavToggle
				:aria-open-side-nav="ariaOpenSideNav"
				:aria-close-side-nav="ariaCloseSideNav"
			/>
		</div>

		<div v-if="items.length > 0" class="header__items">
			<div class="header__link-wrapper">
				<NuxtLinkLocale
					v-for="link in items"
					:key="link.id"
					class="header__link"
					:aria-label="link.fields.link.value.text"
					:class="
						store.context?.itemPath.includes(link?.fields?.link?.value?.href)
							? ' router-link-active'
							: ''
					"
					:to="link.fields.link.value.href"
					@click="clickOnNavLink(link)"
				>
					<SvgIcon
						v-if="link.fields?.icon?.value"
						:icon="link.fields.icon.value"
						class="header__link-icon"
					/>
					<span>{{ link.fields.link.value.text }}</span>
				</NuxtLinkLocale>
			</div>
		</div>
	</header>
</template>

<script setup>
const props = defineProps({
	fields: { type: Object, required: true },
	rendering: { type: Object, required: false },
});

const store = useAppStore();
const isSideNavOpen = computed(() => store.isSideNavOpen);
const items = computed(() => props.fields?.items || []);
const ariaOpenSideNav = computed(
	() => props.fields?.ariaOpenSideNav?.value || "Open side menu"
);
const ariaCloseSideNav = computed(
	() => props.fields?.ariaCloseSideNav?.value || "Close side menu"
);
const ariaHomeLink = computed(
	() => props.fields?.ariaHomeLink?.value || "Home"
);
const showIcon = computed(() => props.fields?.logo?.value);
const scrollLogo = computed(() => props.fields?.scrollLogo?.value);
const pageEl = ref(null);

onMounted(() => {
	pageEl.value = document.querySelector(".page");
	if (pageEl.value) {
		pageEl.value.addEventListener("scroll", onPageScroll);
	}
	onPageScroll();
});

onUnmounted(() => {
	if (pageEl.value) {
		pageEl.value.removeEventListener("scroll", onPageScroll);
	}
});

const isScrolled = ref(false);
function onPageScroll() {
	if (pageEl.value.scrollTop > 20) {
		isScrolled.value = true;
	} else {
		isScrolled.value = false;
	}
}

const { pushGa4 } = useTracking();
function clickOnNavLink(link) {
	pushGa4(link.fields?.gtm?.value);
	store.closeSideNav();
}
</script>

<style lang="scss">
body:has(.component--stickybanner) .header .header__top-center {
	.header__logo--scroll {
		transform: translateY(115px) scale(3);
	}
	.header__logo--scrolled {
		transform: translateY(0) scale(1);
	}
}

.header {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	height: var(--header-height, 64px);
	z-index: 20;
	background: #000;

	.header__top-center {
		position: absolute;
		height: 100%;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;

		.header__logo {
			display: block;

			img {
				padding: 0;
				height: 40px;
				width: auto;
				border-radius: 0;
				margin-top: 2px;
			}
		}

		.header__logo--scroll {
			transform-origin: top center;
			transform: translateY(72px) scale(3);
			transition: transform 0.15s ease-in-out;
		}

		.header__logo--scrolled {
			transform: translateY(0) scale(1);
		}
	}

	.header__top-left {
		z-index: 999;
	}

	&.header--sidenav-open {
		.header__link {
			color: var(--color-black, #000);
		}

		.header__top-row .header__link:last-child {
			background-color: transparent;
		}

		.header__link-icon path {
			fill: var(--color-black, #000);
		}
	}

	.header__link-icon {
		width: 20px;
		height: 20px;
		margin: 0 var(--spacing-05, 4px) var(--spacing-025, 2px)
			var(--spacing-05, 4px);

		path {
			fill: var(--color-white, #fff);
		}
	}

	.header__link {
		color: var(--color-white, #fff);
		font-family: var(--primary-font, sans-serif);
		font-size: var(--text-2xs, 10px);
		font-style: normal;
		font-weight: 400;
		line-height: 140%;
		text-decoration: none;
		padding: var(--spacing-1, 8px);
		height: var(--header-height, 64px);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		transition: color 0.2s ease-in-out;
		flex-grow: 1;
		flex-shrink: 1;
		flex-basis: 0;
		width: 0;
		text-align: center;

		&.router-link-active {
			color: var(--color-black, #000);
			background-color: #fff;

			path {
				fill: var(--color-black, #000);
			}
		}

		svg {
			width: 26px;
			height: 26px;
			margin-bottom: 2px;
		}
	}

	.header__items {
		display: flex;
		align-items: center;
		justify-content: center;
		position: fixed;
		left: 0;
		right: 0;
		bottom: 0;
	}

	.header__link-wrapper {
		background: #191a1a;
		display: flex;
		align-items: center;
		justify-content: center;
		height: var(--header-height, 64px);
		position: fixed;
		bottom: 3vh;
		margin: 0 auto;
		width: 90%;
		overflow: hidden;
		border-radius: 8px;
		box-shadow: 0.5px 0.5px 10px 0 var(--primary-color);

		@media (min-width: 768px) {
			width: 380px;
			bottom: 12vh;
		}
	}
}
</style>
